import React from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import FormInput from '../../../components/ui/FormInput';
import Select from 'react-select';

const ContactInfo = ({ contactDetails, onChange, onAdd, onRemove, onPrimaryChange }) => {
  const handleContactChange = (index, field, value) => {
    if (!contactDetails || !Array.isArray(contactDetails)) return;
    
    const updatedContacts = contactDetails.map((contact, i) => 
      i === index ? { ...contact, [field]: value } : contact
    );
    
    if (onChange) {
      // ReservationCreate için: onChange(updatedContacts)
      // ReservationEdit için: onChange(index, field, value)
      // onChange fonksiyonunun parametrelerini kontrol et
      if (onChange.length === 1) {
        // ReservationCreate durumu
        onChange(updatedContacts);
      } else {
        // ReservationEdit durumu
        onChange(index, field, value);
      }
    }
  };

  const handleSelectChange = (index, selectedOption) => {
    handleContactChange(index, 'type', selectedOption ? selectedOption.value : 'email');
  };

  const handleAddContact = () => {
    if (onAdd) {
      onAdd();
    }
  };

  const handleRemoveContact = (index) => {
    if (onRemove) {
      onRemove(index);
    }
  };

  const handlePrimaryChange = (index) => {
    if (!contactDetails || !Array.isArray(contactDetails)) return;
    
    const updatedContacts = contactDetails.map((contact, i) => {
      if (i === index) {
        return { ...contact, isPrimary: true };
      } else if (contact.type === contactDetails[index].type) {
        // Aynı tipteki diğer kontakları primary olmaktan çıkar
        return { ...contact, isPrimary: false };
      }
      return contact;
    });
    
    if (onChange) {
      // ReservationCreate için: onChange(updatedContacts)
      // ReservationEdit için: onPrimaryChange kullanılıyor
      if (onChange.length === 1) {
        // ReservationCreate durumu
        onChange(updatedContacts);
      } else if (onPrimaryChange) {
        // ReservationEdit durumu - onPrimaryChange kullan
        onPrimaryChange(index);
      }
    }
  };

  // Custom styles for react-select
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? '#3B82F6' : '#D1D5DB',
      boxShadow: state.isFocused ? '0 0 0 2px #DBEAFE' : 'none',
      borderRadius: '0.5rem',
      padding: '2px',
      '&:hover': {
        borderColor: state.isFocused ? '#3B82F6' : '#9CA3AF',
      }
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#3B82F6' : state.isFocused ? '#DBEAFE' : null,
      color: state.isSelected ? 'white' : '#111827',
    }),
  };

  const contactTypeOptions = [
    { value: 'email', label: 'E-posta' },
    { value: 'phone', label: 'Telefon' },
    { value: 'whatsapp', label: 'WhatsApp' }
  ];

  // contactDetails prop'unun güvenli kontrolü
  if (!contactDetails || !Array.isArray(contactDetails)) {
    return (
      <div className="space-y-6">
        <div className="text-center text-gray-500">
          İletişim bilgileri yükleniyor...
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">İletişim Bilgileri</h3>
        <button
          type="button"
          onClick={handleAddContact}
          className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <PlusIcon className="-ml-1 mr-1 h-4 w-4" aria-hidden="true" />
          Yeni Ekle
        </button>
      </div>

      <div className="space-y-4">
        {contactDetails.map((contact, index) => (
          <div key={index} className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 p-4 bg-gray-50 rounded-lg shadow-sm">
            <div className="w-full sm:w-1/4 mb-3 sm:mb-0">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">İletişim Tipi</label>
              <Select
                value={contactTypeOptions.find(option => option.value === contact.type)}
                onChange={(option) => handleSelectChange(index, option)}
                options={contactTypeOptions}
                styles={customStyles}
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>
            
            <div className="flex-1 mb-3 sm:mb-0">
              <FormInput
                type={contact.type === 'email' ? 'email' : 'text'}
                value={contact.value}
                onChange={(e) => handleContactChange(index, 'value', e.target.value)}
                placeholder={
                  contact.type === 'email' 
                    ? 'ornek@email.com' 
                    : contact.type === 'phone' 
                      ? '+90 555 123 4567' 
                      : '+90 555 123 4567'
                }
                label="İletişim Bilgisi"
              />
            </div>
            
            <div className="flex items-center mb-3 sm:mb-0">
              <div className="flex items-center h-full">
                <input
                  type="checkbox"
                  id={`primary-${index}`}
                  checked={contact.isPrimary}
                  onChange={() => handlePrimaryChange(index)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor={`primary-${index}`} className="ml-2 block text-sm text-gray-900">
                  Birincil
                </label>
              </div>
            </div>
            
            <div className="flex items-center justify-end">
              <button
                type="button"
                onClick={() => handleRemoveContact(index)}
                disabled={contactDetails.length <= 1}
                className={`inline-flex items-center p-2 border border-transparent rounded-full shadow-sm text-white ${
                  contactDetails.length <= 1
                    ? 'bg-gray-300 cursor-not-allowed' 
                    : 'bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500'
                }`}
              >
                <TrashIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 bg-yellow-50 border-l-4 border-yellow-400 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-yellow-700">
              En az bir e-posta ve bir telefon numarası ekleyin. Birincil olarak işaretlenen iletişim bilgileri, rezervasyon onayları ve güncellemeleri için kullanılacaktır.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactInfo;
